import {
  Color,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Vector3,
} from '@technology/web3d';
import { MAP_STATION_PATH_COLOR } from '../../capabilities/constants/map-station';
import type { StationInfo } from '@trimble/web3d-providers';
import { STATION_PATH_MESH_TYPE } from '@trimble/types';

export interface StationPathOptions {
  stationsInfo: StationInfo[];
  pathWidth: number;
  pathOpacity: number;
  pathHeightOffset: number;
  renderOrder: number;
}

export interface SinglePathSegmentOptions {
  start: Vector3;
  end: Vector3;
  pathWidth: number;
  pathOpacity: number;
  renderOrder: number;
  color?: Color;
}

const HALF = 0.5;

/**
 * Creates a single path segment mesh between two points.
 * Uses position/rotation instead of matrix.compose for stability (no jitter).
 *
 * @param options - Configuration options for the segment
 * @returns A Mesh representing the path segment
 */
export function createSinglePathSegmentMesh(
  options: SinglePathSegmentOptions
): Mesh {
  const { start, end, pathWidth, pathOpacity, renderOrder, color } = options;

  // Calculate midpoint
  const midpoint = new Vector3(
    (start.x + end.x) * HALF,
    (start.y + end.y) * HALF,
    (start.z + end.z) * HALF
  );

  // Calculate 3D direction and length
  const direction = new Vector3().subVectors(end, start);
  const length = direction.length();

  const geometry = new PlaneGeometry(length, pathWidth);

  // Create individual material for each segment to allow per-segment color control
  const material = new MeshBasicMaterial({
    color: color ?? MAP_STATION_PATH_COLOR,
    transparent: true,
    opacity: pathOpacity,
    side: DoubleSide,
    depthTest: false,
    depthWrite: false,
  });

  const mesh = new Mesh(geometry, material);
  mesh.userData = { type: STATION_PATH_MESH_TYPE };
  mesh.position.copy(midpoint);

  direction.normalize();

  const yaw = Math.atan2(direction.y, direction.x);
  const pitch = Math.asin(-direction.z);

  mesh.rotation.set(0, pitch, yaw, 'ZYX');
  mesh.renderOrder = renderOrder;

  return mesh;
}

/**
 * Creates straight line segments (flat planes) between consecutive stations.
 * Each plane is rotated in 3D space to properly connect stations at different elevations.
 * Only connects stations from the same run (matching runId).
 *
 * PlaneGeometry was chosen over curves (CatmullRomCurve3, TubeGeometry, MeshLine) because
 * those approaches caused z-fighting and visual artifacts. PlaneGeometry provides stable
 * rendering with simple, independent segments (2 triangles each, no overlapping geometry).
 *
 * @param options - Configuration options for the path
 * @param options.stationsInfo - Array of station info (position + runId) to connect (minimum 2 required)
 * @param options.pathWidth - Width of each path segment
 * @param options.pathOpacity - Initial opacity of the path
 * @param options.pathHeightOffset - Height offset below station markers
 * @param options.renderOrder - Render order for proper layering
 * @returns An array of Meshes (one per segment) or null if fewer than 2 stations
 */
export function createStationPathMesh(
  options: StationPathOptions
): Mesh[] | null {
  const {
    stationsInfo,
    pathWidth,
    pathOpacity,
    pathHeightOffset,
    renderOrder,
  } = options;

  const MIN_STATIONS = 2;
  if (stationsInfo.length < MIN_STATIONS) {
    return null;
  }

  const segments: Mesh[] = [];

  for (let i = 0; i < stationsInfo.length - 1; i++) {
    const currentStation = stationsInfo[i];
    const nextStation = stationsInfo[i + 1];

    // Skip if runIds don't match (stations from different runs)
    if (currentStation.runId !== nextStation.runId) {
      continue;
    }

    const start = currentStation.position;
    const end = nextStation.position;

    const startAdjusted = new Vector3(
      start.x,
      start.y,
      start.z - pathHeightOffset
    );

    const endAdjusted = new Vector3(end.x, end.y, end.z - pathHeightOffset);

    const mesh = createSinglePathSegmentMesh({
      start: startAdjusted,
      end: endAdjusted,
      pathWidth,
      pathOpacity,
      renderOrder,
    });

    segments.push(mesh);
  }

  return segments;
}
